import { useState } from 'react';
import { faLockKeyhole, faUserGroup } from '@fortawesome/pro-regular-svg-icons';
import { Avatar, InfoBox } from '@tryhackme/thm-ui-components';
import { SettingCard } from '../setting-card';
import { WarningInfoBox } from '../warning-info-box';
import { useGetAccessMembersQuery } from '../../manage-room.slice';
import { ManageRoomDetail } from '../../manage-room.types';
import { StyledAccessContentWrapper, StyledAccessDescription, StyledAccessLink, StyledAccessTabContainer, StyledMembersContainer, StyledMemberName, StyledMembersContentWrapper, StyledMembersWrapper, StyledMemberDisplayWrapper, StyledOverlappedMemberWrapper, StyledOverlappedMembers, StyledOverlappedMembersContainer, StyledRemainingMembers, StyledShowMembersButton, StyledAllMembersContainer, StyledLink } from './access-tab.styles';
const MEMBERS_THRESHOLD = 5;
export const AccessTab = ({
  roomDetails
}: {
  roomDetails: ManageRoomDetail;
}) => {
  const accessLink = `${process.env.NODE_ENV === 'development' ? 'http://localhost:1337' : window.location.origin.toString()}/api/room/mra?token=${roomDetails?.accessToken}`;
  const [isCollapsed, setIsCollapsed] = useState(true);
  const {
    data
  } = useGetAccessMembersQuery(roomDetails?._id);
  const members = data?.data ?? [];
  const isLessMembers = members.length <= MEMBERS_THRESHOLD;
  const firstFewMembers = isLessMembers ? [] : members.slice(0, MEMBERS_THRESHOLD);
  const showOverlappedMembers = Boolean(firstFewMembers.length) && isCollapsed;
  const showAllMembers = !isLessMembers && !isCollapsed;
  return <StyledAccessTabContainer aria-label="Access Tab" data-sentry-element="StyledAccessTabContainer" data-sentry-component="AccessTab" data-sentry-source-file="access-tab.tsx">
      <SettingCard title="Access" icon={faLockKeyhole} data-sentry-element="SettingCard" data-sentry-source-file="access-tab.tsx">
        <StyledAccessContentWrapper data-sentry-element="StyledAccessContentWrapper" data-sentry-source-file="access-tab.tsx">
          <StyledAccessDescription data-sentry-element="StyledAccessDescription" data-sentry-source-file="access-tab.tsx">
            Give others access to your room and allow them to collaborate by sharing the link below.
          </StyledAccessDescription>

          <InfoBox textToCopy={accessLink} linkButtonType="button" data-sentry-element="InfoBox" data-sentry-source-file="access-tab.tsx">
            <StyledAccessLink data-sentry-element="StyledAccessLink" data-sentry-source-file="access-tab.tsx">{accessLink}</StyledAccessLink>
          </InfoBox>

          <WarningInfoBox warningText="Warning! If multiple access users edit tasks, it will overwrite with the last edit." data-sentry-element="WarningInfoBox" data-sentry-source-file="access-tab.tsx" />
        </StyledAccessContentWrapper>
      </SettingCard>

      <StyledMembersWrapper aria-label="Members" data-sentry-element="StyledMembersWrapper" data-sentry-source-file="access-tab.tsx">
        <SettingCard title="Members" icon={faUserGroup} data-sentry-element="SettingCard" data-sentry-source-file="access-tab.tsx">
          <StyledMembersContentWrapper data-sentry-element="StyledMembersContentWrapper" data-sentry-source-file="access-tab.tsx">
            {isLessMembers && <StyledMembersContainer>
                {members.map(member => <StyledLink key={member._id} to={`/r/p/${member.local.username}`}>
                    <StyledMemberDisplayWrapper>
                      <Avatar hasBorder={false} avatarSrc={member.local.avatar} size="3.2rem" />
                      <StyledMemberName>{member.local.username}</StyledMemberName>
                    </StyledMemberDisplayWrapper>
                  </StyledLink>)}
              </StyledMembersContainer>}

            {showOverlappedMembers && <StyledOverlappedMembers>
                <StyledOverlappedMembersContainer>
                  {firstFewMembers.map(member => <StyledLink key={member._id} to={`/r/p/${member.local.username}`}>
                      <StyledOverlappedMemberWrapper>
                        <Avatar hasBorder avatarSrc={member.local.avatar} size="3.2rem" />
                      </StyledOverlappedMemberWrapper>
                    </StyledLink>)}
                  <StyledRemainingMembers>{`+${members.length - MEMBERS_THRESHOLD}`}</StyledRemainingMembers>
                </StyledOverlappedMembersContainer>
                <StyledShowMembersButton onClick={() => setIsCollapsed(false)}>
                  Show all members
                </StyledShowMembersButton>
              </StyledOverlappedMembers>}

            {showAllMembers && <>
                <StyledAllMembersContainer>
                  {members.map(member => <StyledLink key={member._id} to={`/r/p/${member.local.username}`}>
                      <StyledMemberDisplayWrapper>
                        <Avatar hasBorder={false} avatarSrc={member.local.avatar} size="3.2rem" />
                        <StyledMemberName>{member.local.username}</StyledMemberName>
                      </StyledMemberDisplayWrapper>
                    </StyledLink>)}
                </StyledAllMembersContainer>

                <StyledShowMembersButton onClick={() => setIsCollapsed(true)}>
                  Show less members
                </StyledShowMembersButton>
              </>}
          </StyledMembersContentWrapper>
        </SettingCard>
      </StyledMembersWrapper>
    </StyledAccessTabContainer>;
};